import type { FC } from "react";
import { useState } from "react";
import { useFormContext, useForm, useController } from "react-hook-form";
import { Box, Stack, IconButton, Tooltip } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import { useMutation } from "@tanstack/react-query";
import { TrashCanIcon, PencilIcon, FileTextIcon } from "../../shared/icons";
import type { InputProps } from "../DynamicInput";
import type { MultipleExternalLinkValue } from "../types";
import * as FormDialog from "../../shared/FormDialog";
import { DynamicShared } from "../../dynamicShared";
import { ErrorMessage } from "../inlineForm/ErrorMessage";
import { useDynamicFormState } from "../DynamicFormProvider";
import { isAnswerRequestEmpty } from "./isAnswerRequestEmpty";
import { AddDialogTrigger } from "./AddDialogTrigger";

type LinkValue = MultipleExternalLinkValue[number];

export const MultipleExternalLinkInput: FC<InputProps<MultipleExternalLinkValue>> = ({
  questionCard,
  onSave,
  readonly,
}) => {
  const { t } = useTranslation();
  const { mandatoryQuestionErrorMessage } = useDynamicFormState();
  const [linkModalState, setLinkModalState] = useState<
    { type: "closed" } | { type: "edit"; link: LinkValue; fieldIndex: number }
  >({
    type: "closed",
  });

  const { control } = useFormContext();
  const saveMutation = useMutation({
    mutationFn: (value: MultipleExternalLinkValue) => onSave(value),
    onSuccess: () => setLinkModalState({ type: "closed" }),
  });

  const { field } = useController({ control, name: questionCard.id });

  const fieldValue = (field.value ?? []) as LinkValue[];

  const updateLink = (index: number, value: LinkValue) => {
    const newValue = fieldValue.map((link, i) => (i === index ? value : link));
    return saveMutation.mutateAsync(newValue, { onSuccess: () => field.onChange(newValue) });
  };

  const addLink = (value: LinkValue) => {
    const newValue = [...fieldValue, value];
    return saveMutation.mutateAsync(newValue, { onSuccess: () => field.onChange(newValue) });
  };

  const removeLink = (index: number) => {
    const newValue = fieldValue.filter((_, i) => i !== index);
    field.onChange(newValue);
    return saveMutation.mutateAsync(newValue);
  };

  return (
    <>
      <Stack sx={{ width: "100%", my: fieldValue.length > 0 ? 0.5 : 0 }}>
        {fieldValue.map((link, index) => (
          <LinkItem
            key={`${link.url}-${link.label}`}
            readonly={readonly}
            link={link}
            onRemoveClick={() => removeLink(index)}
            onEditClick={() => setLinkModalState({ type: "edit", link, fieldIndex: index })}
          />
        ))}
        <Box>
          <AddDialogTrigger
            isMultistepFormVersion={false}
            isEmpty={fieldValue.length === 0}
            isReadOnly={readonly}
            renderDialog={props => (
              <LinkModal {...props} onSubmit={addLink} title={t("ExternalMultipleLink.AddLinkTitle")} />
            )}
          >
            {t("ExternalMultipleLink.AddLink")}
          </AddDialogTrigger>

          {saveMutation.isError && <ErrorMessage />}
          {mandatoryQuestionErrorMessage && questionCard.mandatory && isAnswerRequestEmpty(field.value) && (
            <ErrorMessage variant="warning" errorMessage={mandatoryQuestionErrorMessage} />
          )}
        </Box>
      </Stack>

      {linkModalState.type === "edit" && (
        <LinkModal
          onClose={() => setLinkModalState({ type: "closed" })}
          onSubmit={link => updateLink(linkModalState.fieldIndex, link)}
          defaultLink={linkModalState.link}
          title={t("ExternalMultipleLink.EditLinkTitle")}
        />
      )}
    </>
  );
};

type LinkItemProps = {
  link: LinkValue;
  onRemoveClick: () => void;
  onEditClick: () => void;
  readonly: boolean;
};

const LinkItem: FC<LinkItemProps> = ({ link, onRemoveClick, onEditClick, readonly }) => {
  const { t } = useTranslation();
  const [isHovering, setIsHovering] = useState(false);

  const showEditable = !readonly && isHovering;

  return (
    <Box
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      sx={{
        px: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: showEditable ? theme => theme.palette.action.hover : "revert",
        borderRadius: 1,
      }}
    >
      <DynamicShared.ExternalLink
        tabIndex={readonly ? -1 : 0}
        sx={{ pointerEvents: readonly ? "none" : "auto" }}
        title={link.url}
        Icon={FileTextIcon}
        href={link.url}
      >
        {link.label}
      </DynamicShared.ExternalLink>
      <Stack direction="row" sx={{ gap: 0.5, visibility: showEditable ? "visible" : "hidden" }}>
        <Tooltip title={t("ExternalMultipleLink.EditLinkTitle.Tooltip")}>
          <IconButton aria-label={t("ExternalMultipleLink.EditLinkTitle.Tooltip")} onClick={onEditClick}>
            <PencilIcon size={14} />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("ExternalMultipleLink.DeleteLinkTitle.Tooltip")}>
          <IconButton aria-label={t("ExternalMultipleLink.DeleteLinkTitle.Tooltip")} onClick={onRemoveClick}>
            <TrashCanIcon size={14} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
};

type LinkModalProps = {
  onClose: () => void;
  onSubmit: (link: LinkValue) => Promise<unknown>;
  defaultLink?: LinkValue;
  title: string;
};

const LinkModal: React.FC<LinkModalProps> = ({ onClose, onSubmit, defaultLink, title }) => {
  const { t } = useTranslation();
  const { handleSubmit, control, setFocus } = useForm<LinkValue>({
    defaultValues: defaultLink || { label: "", url: "" },
  });
  const submitMutation = useMutation({ mutationFn: onSubmit });

  FormDialog.useAutoFocusField(setFocus, "label");

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{title}</FormDialog.Title>

      <FormDialog.Form onSubmit={handleSubmit(link => submitMutation.mutate(link, { onSuccess: onClose }))}>
        <FormDialog.Content>
          <FormDialog.FormError isError={submitMutation.isError} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="label"
              label={t("ExternalMultipleLink.Label")}
              rules={{ required: t("Input.Validation.Required") }}
              placeholder={t("ExternalMultipleLink.LabelPlaceholder")}
            />

            <FormDialog.TextField
              control={control}
              name="url"
              label={t("ExternalMultipleLink.Url")}
              rules={{ required: t("Input.Validation.Required") }}
              placeholder={t("ExternalMultipleLink.Placeholder")}
              inputMode="url"
              autoComplete="url"
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={submitMutation.isPending}>
            {defaultLink ? t("ExternalMultipleLink.UpdateButton") : t("ExternalMultipleLink.AddButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
